import React, { useEffect, useState } from 'react';
import { animated, useChain, useSpring, useSpringRef } from 'react-spring';
import { useDiscounts } from './Discounts';

interface AnimatedConversionBarProps {
    children: React.ReactNode;
    className?: string;
}

export const AnimatedConversionBar: React.FC<AnimatedConversionBarProps> = ({
    children,
    className,
}) => {
    const [isHydrated, setIsHydrated] = useState(false);
    const { shouldShow, DiscountsComponent } = useDiscounts();

    useEffect(() => {
        setIsHydrated(true);
    }, []);
    const springRef = useSpringRef();
    const [containerSprings] = useSpring(
        () => ({
            ref: springRef,
            from: { transform: 'translateY(180px)', opacity: 0 },
            to: { transform: 'translateY(0)', opacity: 1 },
            config: {
                damping: 15,
                tension: 100,
                precision: 0.0001,
            },
        }),
        [],
    );

    const transRef = useSpringRef();
    const [signalSprings] = useSpring(
        () => ({
            ref: transRef,
            from: { transform: 'translateY(100%)' },
            to: { transform: 'translateY(0)' },
            config: {
                damping: 20,
                tension: 200,
            },
        }),
        [],
    );

    const placeholderRef = useSpringRef();
    const [placeholderSprings] = useSpring(
        () => ({
            ref: placeholderRef,
            from: { height: '0' },
            to: {
                height: 'var(--conversion-bar-signal-height)',
            },
            config: {
                damping: 20,
                tension: 200,
            },
        }),
        [],
    );

    useChain([springRef, transRef, placeholderRef], [0, 1, 2], 1500);

    // Before hydration: render static elements to prevent hydration mismatch
    if (!isHydrated) {
        return (
            <div className={className}>
                {shouldShow && DiscountsComponent && (
                    <>
                        <div />
                        <div className="conversion-bar-signal">
                            <DiscountsComponent />
                        </div>
                    </>
                )}
                {children}
            </div>
        );
    }

    // After hydration: render animated elements
    return (
        <animated.div style={containerSprings} className={className}>
            {shouldShow && DiscountsComponent && (
                <>
                    <animated.div
                        key="discount-placeholder"
                        style={placeholderSprings}
                    />
                    <animated.div
                        key="discount-signal"
                        style={signalSprings}
                        className="conversion-bar-signal"
                    >
                        <DiscountsComponent />
                    </animated.div>
                </>
            )}
            {children}
        </animated.div>
    );
};
